import { useCallback } from 'react';

/**
 * Lightweight analytics abstraction.
 * Forwards events to PostHog when REACT_APP_POSTHOG_KEY is set.
 * Gracefully no-ops when not configured.
 *
 * No PII: only Firebase UID is sent to PostHog.
 */

// PostHog integration (optional)
let _posthog = null;

function initPostHog() {
  if (_posthog || !process.env.REACT_APP_POSTHOG_KEY) return;
  try {
    const posthog = require('posthog-js').default;
    posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
      api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com',
      autocapture: false,
      capture_pageview: false,   // we call page() manually
      persistence: 'localStorage',
      disable_session_recording: true,
    });
    _posthog = posthog;
  } catch {
    _posthog = null;
  }
}

// Standard event names
export const AnalyticsEvents = {
  // Onboarding flow
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_SKIPPED: 'onboarding_skipped',

  // GitHub integration
  GITHUB_CONNECTED: 'github_connected',
  GITHUB_DISCONNECTED: 'github_disconnected',
  REPOS_IMPORTED: 'repos_imported',
  REPOS_SELECTED: 'repos_selected',

  // Analysis
  ANALYSIS_STARTED: 'analysis_started',
  ANALYSIS_COMPLETED: 'analysis_completed',
  ANALYSIS_FAILED: 'analysis_failed',

  // Feature discovery
  FEATURE_DISCOVERED: 'feature_discovered',
  TAB_VISITED: 'tab_visited',

  // Sharing
  SHARE_CREATED: 'share_created',
  SHARE_COPIED: 'share_copied',

  // Export
  EXPORT_STARTED: 'export_started',
  EXPORT_COMPLETED: 'export_completed',

  // User actions
  SIGN_IN: 'sign_in',
  SIGN_OUT: 'sign_out',
  SETTINGS_UPDATED: 'settings_updated',

  // Engagement
  CTA_CLICKED: 'cta_clicked',
  NOTIFICATION_SIGNUP: 'notification_signup',

  // Additional funnel events
  IMPORT_STARTED: 'import_started',
  IMPORT_COMPLETE: 'import_complete',
  IMPORT_ERROR: 'import_error',
  INSIGHT_CLICKED: 'insight_clicked',
  INSIGHT_DISMISSED: 'insight_dismissed',
  REPO_DETAIL_OPENED: 'repo_detail_opened',
  ANALYSIS_VIEWED: 'analysis_viewed',

  // Persona events
  PERSONA_SELECTED: 'persona_selected',
  PERSONA_SWITCHED: 'persona_switched',
  PERSONA_ONBOARDING_STARTED: 'persona_onboarding_started',
  PERSONA_ONBOARDING_COMPLETED: 'persona_onboarding_completed',

  // Deal room events
  DEAL_ROOM_OPENED: 'deal_room_opened',
  DEAL_ROOM_STAGE_CHANGED: 'deal_room_stage_changed',
  COMPARABLE_VIEWED: 'comparable_viewed',

  // Investor events
  INVESTOR_PORTFOLIO_VIEWED: 'investor_portfolio_viewed',
  STARTUP_CARD_CLICKED: 'startup_card_clicked',
  DD_COMPARISON_OPENED: 'dd_comparison_opened',

  // Architect events
  ARCHITECT_ASSESSMENT_STARTED: 'architect_assessment_started',
  BUY_BUILD_SCORECARD_VIEWED: 'buy_build_scorecard_viewed',
  BUY_BUILD_EXPORTED: 'buy_build_exported',

  // Feasibility events
  FEASIBILITY_SCORED: 'feasibility_scored',
  CAPABILITY_MAP_VIEWED: 'capability_map_viewed',

  // Template events
  TEMPLATE_RENDERED: 'template_rendered',
  TEMPLATE_EXPORTED: 'template_exported',

  // API developer events
  API_KEY_CREATED: 'api_key_created',
  USAGE_DASHBOARD_VIEWED: 'usage_dashboard_viewed',

  // Beta & PMF events
  INVITE_ACCEPTED: 'invite_accepted',
  FEEDBACK_SUBMITTED: 'feedback_submitted',
  SEAN_ELLIS_RESPONSE: 'sean_ellis_response',
  DRILL_DOWN_CLICKED: 'drill_down_clicked',
  SCOPE_SELECTED: 'scope_selected',
  GUIDANCE_DISMISSED: 'guidance_dismissed',
};

/**
 * Analytics service singleton
 */
class AnalyticsService {
  constructor() {
    this.enabled = true;
    this.debug = process.env.NODE_ENV === 'development';
    this.queue = [];
    this.userId = null;
    this.sessionId = this.generateSessionId();
    // Init PostHog lazily on first use
    if (typeof window !== 'undefined') {
      initPostHog();
    }
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  setUserId(userId) {
    this.userId = userId;
  }

  enable() {
    this.enabled = true;
  }

  disable() {
    this.enabled = false;
  }

  /**
   * Track an analytics event
   * @param {string} event - Event name from AnalyticsEvents
   * @param {object} properties - Additional event properties
   */
  track(event, properties = {}) {
    if (!this.enabled) return;

    const payload = {
      event,
      properties: {
        ...properties,
        timestamp: new Date().toISOString(),
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        sessionId: this.sessionId,
        userId: this.userId,
      },
    };

    if (this.debug) {
      console.log('[Analytics]', event, payload.properties);
    }

    // Queue for batching
    this.queue.push(payload);

    // Forward to PostHog
    if (_posthog) {
      _posthog.capture(event, payload.properties);
    }
  }

  /**
   * Track a page view
   * @param {string} pageName - Name of the page
   * @param {object} properties - Additional properties
   */
  page(pageName, properties = {}) {
    this.track('page_view', {
      page: pageName,
      ...properties,
    });
  }

  /**
   * Identify a user
   * @param {string} userId - User identifier
   * @param {object} traits - User traits
   */
  identify(userId, traits = {}) {
    this.userId = userId;
    if (this.debug) {
      console.log('[Analytics] Identify', userId, traits);
    }
    if (_posthog) {
      _posthog.identify(userId, traits);
    }
  }

  /**
   * Identify user across all analytics providers
   * @param {string} userId - User identifier (Firebase UID)
   * @param {object} traits - User traits (no PII)
   */
  identifyUser(userId, traits = {}) {
    this.identify(userId, traits);

    // Also forward to window.posthog if loaded externally (e.g. snippet)
    if (typeof window !== 'undefined' && window.posthog && window.posthog !== _posthog) {
      try {
        window.posthog.identify(userId, traits);
      } catch {
        // no-op — external PostHog may not be ready
      }
    }
  }

  /**
   * Track an event with persona context attached
   * @param {string} eventName - Event name
   * @param {string} persona - Persona ID (e.g. 'professional', 'founder')
   * @param {object} properties - Additional event properties
   */
  trackPersonaEvent(eventName, persona, properties = {}) {
    this.track(eventName, {
      persona,
      persona_context: true,
      ...properties,
    });
  }

  /**
   * Access PostHog instance (if available)
   * Returns the initialized PostHog client or null
   */
  get posthog() {
    // Prefer the SDK-initialized instance, fall back to window global
    return _posthog || (typeof window !== 'undefined' ? window.posthog : null) || null;
  }

  /**
   * Reset user identity (on logout)
   */
  reset() {
    this.userId = null;
    this.sessionId = this.generateSessionId();
    if (this.debug) {
      console.log('[Analytics] Reset');
    }
    if (_posthog) {
      _posthog.reset();
    }
  }

  /**
   * Get queued events (for debugging)
   */
  getQueue() {
    return [...this.queue];
  }

  /**
   * Clear the queue
   */
  clearQueue() {
    this.queue = [];
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();

/**
 * React hook for easy analytics tracking
 * @returns {object} - trackEvent function and events constant
 */
export const useAnalytics = () => {
  const trackEvent = useCallback((event, props = {}) => {
    analytics.track(event, props);
  }, []);

  const trackPage = useCallback((pageName, props = {}) => {
    analytics.page(pageName, props);
  }, []);

  const identify = useCallback((userId, traits = {}) => {
    analytics.identify(userId, traits);
  }, []);

  return {
    trackEvent,
    trackPage,
    identify,
    events: AnalyticsEvents,
  };
};

export default analytics;
